import { useQueries, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import semver from 'semver';

import { Connection as GraphConnection } from 'src/ui/graph/types';
import {
  ServerStatus,
  serverStatusQueryConfig,
  serverVersionFromStatus,
  useConnectionServerStatus,
} from 'src/ui/hooks/serverStatus';

/** Coerce the raw `dbms.version` to semver, defaulting to 0.0.0 when unparseable. */
const coerceEndpointVersion = (
  status: ServerStatus | null | undefined
): string | null => {
  const version = serverVersionFromStatus(status);
  if (version === null) {
    return null;
  }
  return semver.coerce(version)?.version || '0.0.0';
};

export const useEndpointVersion = (
  connection: GraphConnection
): string | null => {
  const { org = '' } = useParams<{ org?: string }>();
  const { data } = useConnectionServerStatus(connection, org);
  return coerceEndpointVersion(data);
};

export const useEndpointVersions = (
  connections: readonly GraphConnection[]
): Record<string, string | null> => {
  const { org = '' } = useParams<{ org?: string }>();
  const queryClient = useQueryClient();
  const results = useQueries(
    connections.map((c) => serverStatusQueryConfig(queryClient, c, org))
  );
  const map: Record<string, string | null> = {};
  results.forEach((r, idx) => {
    map[connections[idx].id] = coerceEndpointVersion(
      r.data as ServerStatus | null | undefined
    );
  });
  return map;
};

export const useEndpointVersionGate = (
  connection: GraphConnection,
  minVersion: string,
  maxVersion?: string
) => {
  const version = useEndpointVersion(connection);

  if (!version) {
    return false;
  }

  if (maxVersion && !semver.lte(version, maxVersion)) {
    return false;
  }

  return semver.gte(version, minVersion);
};
